'use client';

import {Suspense} from 'react';
import {keepPreviousData, useQuery} from '@tanstack/react-query';
import clsx from 'clsx';
import {useSearchParams} from 'next/navigation';

import type {FaqsApiResponse} from '@/app/(frontend)/api/faqs/route';
import {Alert} from '@/modules/finkn/components/alert';
import type {FieldOfAreaFilters} from '@/modules/finkn/shared/components/filters/category-filters';
import {CategoryFilters} from '@/modules/finkn/shared/components/filters/category-filters';
import {
	QUERY_PARAM_DELIMITER,
	searchParamsToFilters,
} from '@/modules/finkn/shared/components/filters/search-params-filter';
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionItemSkeleton,
	AccordionTrigger,
} from '@/modules/foundation/components/accordion';
import type {ContentBlockContainerProps} from '@/modules/page/components/content-blocks/block-container';
import {ContentBlockContainer} from '@/modules/page/components/content-blocks/block-container';

import type {FaqInCardFragment} from '../../../sanity/query';
import {
	type FaqAreaFilters,
	faqInCardFragmentToFaq,
} from '../../../sanity/query';

interface Props extends ContentBlockContainerProps {
	initialFaqs?: FaqInCardFragment[];
	fieldOfAreaFilters?: FieldOfAreaFilters[];
	faqAreaFilters: FaqAreaFilters[];
	insideToc?: boolean;
}

const fetchFaqs = async (
	areas?: string[],
	fieldOfAreas?: string[],
): Promise<FaqsApiResponse> => {
	const areasString = areas?.join(QUERY_PARAM_DELIMITER) ?? '';
	const fieldOfAreasString = fieldOfAreas?.join(QUERY_PARAM_DELIMITER) ?? '';

	let query = `/api/faqs?page=1`;

	if (areas?.length) {
		query = query + `&areas=${areasString}`;
	}

	if (fieldOfAreas?.length) {
		query = query + `&fieldOfAreas=${fieldOfAreasString}`;
	}

	const response = await fetch(query);
	return await response.json();
};

export const FaqListAll = ({
	initialFaqs,
	fieldOfAreaFilters,
	faqAreaFilters,
	options,
	insideToc,
}: Props) => {
	const searchParams = useSearchParams();

	const filters = searchParamsToFilters(searchParams);

	const {status, data, error, isFetching} = useQuery({
		queryKey: ['faqs', filters.area, filters.fieldOfArea],
		queryFn: () => fetchFaqs(filters.area, filters.fieldOfArea),
		placeholderData: keepPreviousData,
		staleTime: 5 * 60 * 1000, // 5 minutes
		initialData:
			initialFaqs && !filters.area.length && !filters.fieldOfArea.length
				? {
						faqs: initialFaqs,
						faqAreaFilters,
					}
				: undefined,
	});

	const faqs = data?.faqs.map((faq) => {
		const transformedArticle = faqInCardFragmentToFaq(faq);
		return {...transformedArticle, _key: faq._id};
	});

	const filteredFaqs = faqs?.filter((faq) => {
		const {fieldOfArea, area} = filters;

		// If no filters are applied, include all FAQs
		if (!fieldOfArea.length && !area.length) {
			return true;
		}

		// Check if the FAQ matches any of the fieldOfArea filters
		const fieldOfAreaMatch = fieldOfArea.some((filter) =>
			faq.fieldOfAreas?.includes(filter),
		);

		// If no fieldOfArea filters are applied, skip area filtering
		if (!fieldOfArea.length) {
			return true;
		}

		// If there are fieldOfArea matches and no area filters, include the FAQ
		if (fieldOfAreaMatch && !area.length) {
			return true;
		}

		// If both fieldOfArea and area filters are applied, check for both matches
		const areaMatch = area.some((filter) => faq.areas?.includes(filter));
		return fieldOfAreaMatch && areaMatch;
	});

	return (
		<ContentBlockContainer options={options} insideToc={insideToc}>
			<div className="@container">
				<section className={clsx('grid', '@2xl:grid-cols-3', 'gap-10')}>
					<aside className={clsx('@2xl:col-span-1')}>
						<Suspense>
							<CategoryFilters
								fieldOfAreaFilters={fieldOfAreaFilters}
								areaFilters={data?.faqAreaFilters}
							/>
						</Suspense>
					</aside>

					{
						// Since the last page's data potentially sticks around between page requests,
						// we can use `isFetching` to show a background loading
						// indicator since our `status === 'pending'` state won't be triggered
					}
					<div className={clsx('@2xl:col-span-2', 'max-w-[740px]')}>
						{status === 'pending' || isFetching ? (
							<div className={clsx('space-y-1')}>
								<AccordionItemSkeleton />
								<AccordionItemSkeleton />
								<AccordionItemSkeleton />
								<AccordionItemSkeleton />
								<AccordionItemSkeleton />
							</div>
						) : status === 'error' ? (
							<div>Error: {error.message}</div>
						) : (
							// `data` will either resolve to the latest page's data
							// or if fetching a new page, the last successful page's data
							<Accordion type="multiple">
								{filteredFaqs && filteredFaqs.length ? (
									<>
										{filteredFaqs.map((faq) => {
											if (!faq) {
												return null;
											}

											return (
												<AccordionItem
													key={faq._key}
													value={faq._key}
												>
													<AccordionTrigger>
														{faq.question}
													</AccordionTrigger>
													<AccordionContent>
														{faq.answer}
													</AccordionContent>
												</AccordionItem>
											);
										})}
									</>
								) : (
									<Alert title="Valgte filtre gir ingen treff" />
								)}
							</Accordion>
						)}
					</div>
				</section>
			</div>
		</ContentBlockContainer>
	);
};
